import React from "react";
import LegalPage from "@/components/legal/LegalPage";

export default function Disclaimer() {
  return (
    <LegalPage
      pageTitle="Earnings Disclaimer"
      slug="disclaimer"
      seoDescription="JobHacki earnings disclaimer: results are not typical, no income is guaranteed, playbooks are educational, and nothing here is financial or career advice."
      kicker="EARNINGS DISCLAIMER"
      title={<>We don&apos;t guarantee <span className="bg-[#0a0a0a] text-[#FFE600] px-2">you&apos;ll make money.</span></>}
      effectiveDate="June 11, 2026"
      accent="#FF3B30"
      intro={<><b>The short version:</b> JobHacki documents real workflows, but what you earn — if anything — depends on you, your effort, your skills, and your market. Anyone who promises otherwise is selling you something.</>}
      sections={[
        {
          title: "Results are not typical",
          body: (
            <p>Any income figures, outcomes, or examples mentioned anywhere on JobHacki — in playbooks, on product pages, in emails, or in our content — describe specific situations and are <b>not typical results</b>. They are not a promise, projection, or guarantee of what you will earn. Most people who buy educational material of any kind get no measurable result, usually because they never implement it.</p>
          ),
        },
        {
          title: "No income guarantees",
          body: (
            <p>We make <b>no guarantee of income, revenue, savings, job offers, interviews, or any other outcome</b> from using JobHacki, its playbooks, or its tools. The one metric we publish — our live member count — is a measure of how many people have joined, not of what anyone has earned.</p>
          ),
        },
        {
          title: "Playbooks are educational",
          body: (
            <>
              <p>Every playbook and tool on JobHacki is <b>educational material</b>: it documents a workflow — the tools, the steps, the order. Whether that workflow produces anything for you depends on factors we don&apos;t control, including:</p>
              <ul className="space-y-2 list-disc pl-5">
                <li><b>Your effort and consistency</b> — a playbook you don&apos;t execute produces nothing.</li>
                <li><b>Your skills and starting point</b> — experience, time available, and resources vary widely.</li>
                <li><b>Market conditions</b> — demand, competition, platform rules, and the economy change constantly. A workflow that worked when it was documented may work differently, or not at all, later.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Not financial, legal, tax, or career advice",
          body: (
            <p>Nothing on JobHacki is financial advice, investment advice, legal advice, tax advice, or professional career counseling. We&apos;re not your financial advisor, lawyer, accountant, or career counselor. Before making decisions that affect your money, taxes, employment, or legal standing, consult a licensed professional who knows your situation.</p>
          ),
        },
        {
          title: "Affiliate and external links",
          body: (
            <ul className="space-y-2 list-disc pl-5">
              <li><b>Some links may be affiliate links</b>, meaning we may earn a commission if you sign up or buy through them — at no extra cost to you. We link to tools because they&apos;re part of a documented workflow, not because of commissions.</li>
              <li><b>External sites are not ours.</b> We don&apos;t control third-party sites or services and aren&apos;t responsible for their content, pricing, policies, or conduct. Do your own due diligence before paying anyone for anything.</li>
            </ul>
          ),
        },
        {
          title: "Your decisions are yours",
          body: (
            <p>By using JobHacki you agree that you are responsible for your own decisions and results. You assess the risks, you do the work, and you own the outcome — good or bad. To the maximum extent permitted by law, JobHacki is not liable for any losses or damages arising from decisions you make based on our content. See the <a className="font-bold border-b-2 border-current" href="/terms">Terms of Service</a> for the full limitation of liability.</p>
          ),
        },
      ]}
    />
  );
}
